import React, { useState, useEffect } from 'react'
import { bool, func, number, string, shape } from 'prop-types'
import { css } from '@emotion/core'
import { useAtSize, useOverlay } from '@fs/zion-ui'
import { ContentAdd } from '@fs/zion-icon'
import { useAamState } from '../../state/AamStore'
import useFamilyTreeData from '../../../../shared/hooks/useFamilyTreeData'
import SliderProfileImage from './SliderProfileImage'
import MissingBirthDateDialog from '../MissingBirthDateDialog/MissingBirthDateDialog'
import { useCorrections } from '../../hooks/hooks'

const sliderContainer = (isCorrections) => css`
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  padding-bottom: 18px;
  ${isCorrections && 'padding-left: 32px;'}
  scrollbar-color: #444 rgba(0, 0, 0, 0.75);
  scrollbar-width: auto;
  ::-webkit-scrollbar {
    width: 15px;
  }
  ::-webkit-scrollbar-track {
    box-shadow: inset 0 0 2px grey;
    border-radius: 0px;
  }
  ::-webkit-scrollbar-thumb {
    background: #444444;
    border-radius: 8px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: #555555;
  }
  :hover {
    scrollbar-color: #555;
  }
`
const ancestorSliderRootCss = css`
  --profileHalfWidth: 54px;
  width: 100%;
  padding-top: 10px;
  background-color: rgba(0, 0, 0, 0.75);
  .portrait {
    display: inline-block;
    width: 120px;
    min-width: 120px;
    background-color: transparent;
    border: none;
    margin: 0;
    padding: 0;
    outline: none;
    cursor: pointer;
    touch-action: manipulation;
    opacity: 0.4;
    transition: opacity 0.2s ease-in-out;
  }
  .details {
    width: auto;
    text-align: center;
    color: #f7f8f8;
    white-space: nowrap;
    opacity: 0;
    transform: translateX(-10%);
    transition: opacity 0.2s ease-in-out;
  }
  .portrait.active,
  .details.active {
    opacity: 1;
  }
  .portrait.active [sex='male'] {
    border: 3px solid var(--sex-male);
  }
  .portrait.active [sex='female'] {
    border: 3px solid var(--sex-female);
  }
  .portrait [sex='female'] svg {
    transform: rotateY(180deg);
  }
  .name {
    font-size: 14px;
    color: #fff;
  }
  .relation {
    line-height: 24px;
    height: 16px;
    text-transform: uppercase;
    font-size: 11px;
  }
`

const atfBtnCss = css`
  display: inline-block;
  background-color: #f0f0f0;
  border: 3px solid hsla(0, 0%, 0%, 0.1);
  border-radius: 50%;
  outline: none;
  min-width: 88px;
  height: 88px;
  margin: 0 15px;
  &:hover {
    cursor: pointer;
  }
`

const sortFamilyFirst = (a) => {
  const isDescendant = typeof a.atn === 'string' && a.atn && a.atn.startsWith('1.')
  if (isDescendant) {
    return -1
  }
  return 0
}

export default function AncestorSlider({ updateTileContent, toggleAtf, tileData }) {
  const [treeDataState] = useFamilyTreeData(3)
  const [tree, setTree] = useState([])
  const [activePerson, setActivePerson] = useState({})
  const [needsFormatting, setNeedsFormatting] = useState('')
  const [eventLog, setEventLog] = useState([])
  const [isMissingDOB, setIsMissingDOB] = useState(false)
  const aamState = useAamState()
  const atSize = useAtSize()
  const missingBirthDateOverlay = useOverlay()
  const isCorrections = useCorrections()

  const changeActiveAncestor = (event, index, guestUser, ancestor) => {
    let newTree
    if (!guestUser) {
      newTree = tree.map((ans) => {
        if (ans.personId === tileData.personId) {
          return ans
        }
        ans.active = false
        return ans
      })
    } else {
      newTree = aamState?.guestTree.map((ans) => {
        if (ans.displayName === tileData.header) {
          return ans
        }
        ans.active = false
        return ans
      })
    }
    newTree[index].active = true
    const activeArray = []

    newTree.forEach((ans, i) => {
      if (ans.active === true) {
        activeArray.push(i)
      }
    })

    if (activeArray.length > 1 && activeArray[0] + 1 === activeArray[1]) {
      setNeedsFormatting(activeArray[0])
    } else {
      setNeedsFormatting('')
    }

    setTree(newTree)
    if (tileData.needsDOB && (!ancestor?.dob || ancestor?.dob === 'Living')) {
      setActivePerson(ancestor)
      setIsMissingDOB(true)
      missingBirthDateOverlay.open()
    }
  }

  useEffect(() => {
    // give local state control for active state
    if (treeDataState.complete && treeDataState.res.length) {
      const treeRes = [...treeDataState.res]
      const userProfile = treeRes.shift()
      userProfile.active = true
      treeRes.sort(sortFamilyFirst)
      treeRes.unshift(userProfile)
      const newTree = treeRes.map((ans) => {
        if (ans.personId === tileData.personId) {
          ans.active = true
          return ans
        }
        ans.active = false
        return ans
      })
      setTree(newTree)
    }
  }, [tileData.header, tileData.personId, tileData.year, treeDataState])

  useEffect(() => {
    if (aamState?.guestTree && aamState?.guestTree.length !== 0 && aamState?.isGuestUser) {
      aamState.guestTree.forEach((person, index) => {
        if (index === 0) {
          aamState.guestTree[0].active = true
        } else {
          aamState.guestTree[index].active = false
        }
      })
    }
  }, [aamState.guestTree, aamState.isGuestUser])

  return (
    <div css={ancestorSliderRootCss}>
      <div css={sliderContainer(isCorrections)}>
        {!isCorrections && (
          <button type="button" css={atfBtnCss} onClick={() => toggleAtf()}>
            <ContentAdd />
          </button>
        )}
        {!aamState.isGuestUser
          ? tree.map((ancestor, index) => (
              <button
                type="button"
                className={`portrait ${ancestor.active ? 'active' : ''}`}
                onClick={(event) => {
                  changeActiveAncestor(event, index, false, ancestor)
                  !(tileData.needsDOB && !ancestor.dob) && updateTileContent(event, ancestor)
                  setEventLog(event)
                  setActivePerson(ancestor)
                }}
                key={`${ancestor.personId}_${ancestor.atn}`}
              >
                <SliderProfileImage
                  isActive={ancestor.active}
                  needsFormatting={index === needsFormatting}
                  {...ancestor}
                  diameter={atSize({ default: '88px' })}
                />
              </button>
            ))
          : aamState?.guestTree.map((ancestor, index) => (
              <button
                type="button"
                className={`portrait ${ancestor.active ? 'active' : ''}`}
                onClick={(event) => {
                  changeActiveAncestor(event, index, true, ancestor)
                  !(tileData.needsDOB && !ancestor.dob) && updateTileContent(event, ancestor)
                  setEventLog(event)
                  setActivePerson(ancestor)
                }}
                key={`${ancestor.atn}`}
              >
                <SliderProfileImage
                  isActive={ancestor.active}
                  needsFormatting={index === needsFormatting}
                  {...ancestor}
                  diameter={atSize({ default: '88px' })}
                />
              </button>
            ))}
      </div>
      {isMissingDOB && !aamState?.isGuestUser && (
        <MissingBirthDateDialog
          onUpdate={() => {
            updateTileContent(eventLog, activePerson)
          }}
          overlay={missingBirthDateOverlay}
          person={activePerson}
        />
      )}
    </div>
  )
}

AncestorSlider.propTypes = {
  onClick: func,
  autoScroll: bool,
  toggleAtf: func,
  tileData: shape({
    personId: string,
    year: number,
    header: string,
  }),
}
